import React from "react";
import { Target, Award, CheckCircle, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

export default function MissionSection() {
  const highlights = [
    "Affordable & accessible tutoring",
    "Elite college student tutors",
    "Personalized learning approach",
    "Fair tutor compensation"
  ];

  return (
    <div
      className="
        py-20 relative overflow-hidden transition-colors duration-300
        bg-gradient-to-br from-white via-blue-50 to-purple-50
        dark:bg-gradient-to-b dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950
      "
    >
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-20 dark:opacity-30">
        <div className="absolute top-20 right-0 w-96 h-96 bg-blue-300 dark:bg-red-600/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-0 w-96 h-96 bg-purple-300 dark:bg-orange-500/30 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* LEFT */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="
                inline-flex items-center gap-2 px-6 py-3 rounded-full mb-8 shadow-lg border-2
                bg-gradient-to-r from-blue-100 to-purple-100
                dark:bg-gradient-to-r dark:from-red-500/10 dark:to-orange-500/10
                text-blue-700 dark:text-orange-300
                border-blue-200 dark:border-red-500/40
                backdrop-blur-sm
              "
              whileHover={{ scale: 1.05 }}
            >
              <Target className="w-5 h-5" />
              <span className="font-bold text-lg">Our Mission</span>
            </motion.div>

            <h2
              className="
                text-4xl md:text-6xl font-extrabold mb-8 leading-tight
                bg-gradient-to-r from-blue-900 via-purple-900 to-blue-900
                dark:from-red-400 dark:via-orange-400 dark:to-amber-400
                bg-clip-text text-transparent
              "
            >
              Making Quality Education Accessible
            </h2>

            <div className="space-y-6 text-lg text-gray-700 dark:text-zinc-300 leading-relaxed mb-8">
              <p className="text-xl">
                Our tutoring service is designed to provide{" "}
                <span className="
                  font-bold px-0.5 py-1 rounded
                  text-blue-700 bg-blue-50
                  dark:text-red-300 dark:bg-red-500/10
                ">
                affordable, high-quality academic support
                </span>{" "}
                for students in grades 5th through 12th.
              </p>

              <p>
                Our objective is simple: make tutoring accessible and high quality,
                while ensuring tutors are fairly compensated. They are the{" "}
                <span className="
                  font-bold px-0.5 py-1 rounded
                  text-purple-700 bg-purple-50
                  dark:text-orange-300 dark:bg-orange-500/10
                ">
                  elite among high school graduates and top performing college students
                </span>{" "}
                of their year.
              </p>

              <p>
                Your students connect with knowledgeable and relatable peers, and we
                aim to create a learning environment that is{" "}
                <span className="
                  font-bold px-0.5 py-1 rounded
                  text-indigo-700 bg-indigo-50
                  dark:text-amber-300 dark:bg-amber-500/10
                ">
                  approachable, effective, and personalized.
                </span>
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {highlights.map((highlight, index) => (
                <motion.div
                  key={index}
                  className="
                    flex items-center gap-3 rounded-xl p-4 shadow-md backdrop-blur-sm
                    bg-white/80 border border-blue-100
                    dark:bg-zinc-900/70 dark:border-red-500/20
                  "
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="
                    w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
                    bg-gradient-to-br from-green-400 to-emerald-500
                    dark:from-red-500 dark:to-orange-600
                  ">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-800 dark:text-zinc-200">
                    {highlight}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* RIGHT CARD */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative">
              {/* Glow */}
              <motion.div
                className="
                  absolute inset-0 rounded-3xl blur-xl opacity-30
                  bg-gradient-to-br from-blue-400 via-purple-400 to-pink-400
                  dark:from-red-500 dark:via-orange-500 dark:to-amber-500
                "
                animate={{ rotate: [3, -3, 3] }}
                transition={{ duration: 5, repeat: Infinity }}
              />

              <div className="
                relative rounded-3xl p-10 shadow-2xl border-4 overflow-hidden transition-colors
                bg-white border-white/50
                dark:bg-zinc-900/90 dark:border-red-500/30
              ">
                {/* Corner accents */}
                <div className="
                  absolute top-0 right-0 w-32 h-32 rounded-bl-full opacity-10 dark:opacity-20
                  bg-gradient-to-br from-blue-400 to-purple-500
                  dark:from-red-500 dark:to-orange-500
                " />
                <div className="
                  absolute bottom-0 left-0 w-32 h-32 rounded-tr-full opacity-10 dark:opacity-20
                  bg-gradient-to-tr from-pink-400 to-orange-400
                  dark:from-orange-500 dark:to-amber-500
                " />

                <div className="relative">
                  <div className="flex items-center gap-4 mb-8">
                    <motion.div
                      className="
                        w-16 h-16 rounded-2xl flex items-center justify-center shadow-xl
                        bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600
                        dark:from-red-500 dark:via-orange-500 dark:to-amber-500
                      "
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <Award className="w-8 h-8 text-white" />
                    </motion.div>

                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-3xl font-black text-gray-900 dark:text-white">
                          Our Goal
                        </h3>
                        <Sparkles className="w-6 h-6 text-yellow-500 dark:text-amber-400" />
                      </div>
                      <p className="text-blue-600 dark:text-red-300 font-semibold">
                        Empowering Every Student
                      </p>
                    </div>
                  </div>

                  <p className="text-gray-800 dark:text-zinc-200 leading-relaxed text-lg mb-6">
                    We're dedicated to helping teenagers succeed by providing
                    high-quality, affordable academic support. We believe that{" "}
                    <span className="
                      font-bold px-0.5 py-1 rounded
                      text-blue-700 bg-blue-50
                      dark:text-red-300 dark:bg-red-500/10
                    ">
                      every student deserves access
                    </span>{" "}
                    to the knowledge and expertise they need to grow.
                  </p>

                  <div className="pt-6 border-t-2 border-gray-200 dark:border-zinc-700">
                    <p className="text-gray-800 dark:text-zinc-200 leading-relaxed text-lg">
                      Our tutors are committed to making learning{" "}
                      <span className="
                        font-bold px-0.5 py-1 rounded
                        text-purple-700 bg-purple-50
                        dark:text-orange-300 dark:bg-orange-500/10
                      ">
                        engaging, approachable, and effective
                      </span>, while tailoring each session to fit the unique needs
                      of every student — empowering them with the{" "}
                      <span className="
                        font-bold px-0.5 py-1 rounded
                        text-indigo-700 bg-indigo-50
                        dark:text-amber-300 dark:bg-amber-500/10
                      ">
                        skills and confidence
                      </span>{" "}
                      they need to succeed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}